import type { JSX, ReactNode } from 'react';

/**
 * Заготовка страницы вместо крутящегося кружка.
 *
 * Кружок сообщает только одно: «ждите». Он не говорит, сколько ждать и что
 * появится, и на медленной связи страница на секунду становится пустой —
 * человек успевает решить, что приложение сломалось, и нажать «назад».
 *
 * Заготовка отвечает на оба вопроса сразу: на месте будущих карточек стоят их
 * же прямоугольники, в том же числе и той же формы. Когда данные приезжают,
 * ничего не прыгает — раскладка уже занята, меняется только содержимое.
 *
 * Классы взяты БОЕВЫЕ (`.lists-grid`, `.list-card`, `.items-grid`, `.item`),
 * а не свои похожие. Похожие разъехались бы с настоящей карточкой при первой
 * же правке сетки, и заготовка начала бы врать про раскладку — то есть делать
 * ровно то, ради предотвращения чего она и нужна.
 */

/**
 * Общая обёртка.
 *
 * `role="status"` с подписью — чтобы читающий с экрана услышал «загружаем», а
 * не молчание: сами прямоугольники для него пустое место и помечены
 * `aria-hidden`. `aria-busy` говорит вспомогательным программам, что область
 * ещё меняется.
 */
function Заготовка({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="sk" role="status" aria-busy="true">
      <span className="visually-hidden">{label}</span>
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

/** Полоска текста. Ширина задаётся долей, чтобы строки не были одинаковыми. */
function Строка({ width, height }: { width: string; height?: number }): JSX.Element {
  return <span className="skeleton sk-line" style={{ width, ...(height ? { height } : {}) }} />;
}

/**
 * Заготовка карточек списков — главная и «Друзьям».
 *
 * Три штуки по умолчанию: столько влезает в первый ряд на обычном экране, а
 * обещать больше, чем придёт, — тот же обман, что и обещать меньше.
 */
export function SkeletonLists({ count = 3, label = 'Загружаем списки' }: { count?: number; label?: string }): JSX.Element {
  return (
    <Заготовка label={label}>
      <div className="lists-grid">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className="list-card sk-card">
            <span className="skeleton sk-mosaic" />
            <Строка width="70%" height={16} />
            <Строка width="40%" />
            <span className="sk-chips">
              <span className="skeleton sk-chip" />
            </span>
          </div>
        ))}
      </div>
    </Заготовка>
  );
}

/**
 * Заготовка сетки желаний — страница списка.
 *
 * Обложка держит `aspect-ratio: 1` от боевого `.item__media`, поэтому высота
 * заготовки совпадает с высотой будущей карточки с точностью до строки текста.
 */
export function SkeletonItems({ count = 6, label = 'Загружаем желания' }: { count?: number; label?: string }): JSX.Element {
  return (
    <Заготовка label={label}>
      <div className="items-grid">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className="item sk-card">
            <div className="item__media skeleton" />
            <div className="item__body sk-body">
              <Строка width="80%" height={15} />
              <Строка width="45%" />
              <Строка width="60%" />
            </div>
          </div>
        ))}
      </div>
    </Заготовка>
  );
}

/**
 * Заготовка строк с людьми — друзья, брони, профиль.
 *
 * Кружок аватара тут не украшение: именно по нему человек узнаёт, что сейчас
 * появится список людей, а не карточки товаров.
 */
export function SkeletonRows({ count = 4, label = 'Загружаем' }: { count?: number; label?: string }): JSX.Element {
  return (
    <Заготовка label={label}>
      <div className="stack stack--sm">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className="sk-row">
            <span className="skeleton sk-avatar" />
            <span className="sk-row__text">
              <Строка width="45%" height={14} />
              <Строка width="28%" />
            </span>
          </div>
        ))}
      </div>
    </Заготовка>
  );
}

/**
 * Заготовка шапки страницы: заголовок и подпись под ним.
 *
 * Идёт над сеткой там, где шапка приезжает вместе с данными (страница списка):
 * без неё сетка при загрузке стоит вплотную к меню, а после загрузки уезжает
 * вниз на высоту заголовка — и человек теряет то место, куда смотрел.
 */
export function SkeletonHeader(): JSX.Element {
  return (
    <div className="sk sk-header" aria-hidden="true">
      <Строка width="min(320px, 60%)" height={30} />
      <Строка width="min(220px, 40%)" />
    </div>
  );
}
